import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { useCopilotSessions } from '@/features/copilot/history'
import { toInstant } from '@/features/anomalies/api'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/cn'

/**
 * This person's earlier chats, newest activity first.
 *
 * Picking one opens it in the dock; picking the one already open goes back to it.
 * Switching is refused while an answer is being written — the open chat's stream
 * would be cut off mid-answer, and the server would go on writing it to a chat
 * nobody is looking at.
 *
 * There is no rename and no delete: the API has neither (BACKEND-GAPS.md). Titles
 * are the opening question, set when the chat was created.
 *
 * @param {object} props
 * @param {string|null} props.currentId the open chat's session, null for a new one
 * @param {boolean} props.busy
 * @param {(session: {id: string, title: string|null}) => void} props.onPick
 */
export function CopilotChats({ currentId, busy, onPick }) {
  const { t } = useTranslation(['app', 'common'])
  const sessions = useCopilotSessions()
  const rows = sessions.data?.pages.flatMap((page) => page.sessions) ?? []

  return (
    <div className="flex-1 overflow-y-auto p-2">
      {busy ? <p className="m-0 px-2 py-2 text-micro font-normal text-ink-dim">{t('dock.chats.busy')}</p> : null}

      {sessions.isPending ? (
        <p className="m-0 px-2 py-2 text-body-sm text-ink-dim">{t('dock.restoring')}</p>
      ) : sessions.isError ? (
        <p className="m-0 px-2 py-2 text-body-sm text-danger">{t('dock.chats.failed')}</p>
      ) : rows.length === 0 ? (
        <p className="m-0 px-2 py-2 text-body-sm text-ink-dim">{t('dock.chats.empty')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {rows.map((session) => {
            const current = session.id === currentId
            return (
              <li key={session.id}>
                <button
                  type="button"
                  disabled={busy && !current}
                  aria-current={current ? 'true' : undefined}
                  onClick={() => onPick(session)}
                  className={cn(
                    'flex w-full flex-col items-start rounded-xs px-2 py-2 text-start transition-colors',
                    'hover:bg-sunken disabled:pointer-events-none disabled:opacity-40',
                    current && 'bg-sunken',
                  )}
                >
                  <span className="w-full truncate text-body-sm text-ink" title={session.title ?? undefined}>
                    {session.title || t('dock.chats.untitled')}
                  </span>
                  <span className="text-micro font-normal text-ink-dim">
                    {current ? `${t('dock.chats.current')} · ` : null}
                    {t('dock.chats.updated', { when: formatRelative(toInstant(session.updated_at)) })}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {sessions.hasNextPage ? (
        <div className="px-2 py-2">
          <ForgeButton size="sm" disabled={sessions.isFetchingNextPage} onClick={() => sessions.fetchNextPage()}>
            {t('dock.chats.loadMore')}
          </ForgeButton>
        </div>
      ) : null}
    </div>
  )
}
